import { createAsyncThunk } from '@reduxjs/toolkit'
import * as sendLeadClient from '../../clients/sendLeadClient'
import { PropertyLocation } from '../../@types/location'
import { User } from '../../@types/user'
import { Lead } from '../../@types/lead'
import { Resource } from '../../@types/resource'
import { Valuation } from '../../@types/valuation'
import { isDuplicateUser, setRleCookie } from '../../utils/cookie'
import { reportEvent, reportSubmitLeadEvent } from '../../tracking/tracking'
import { EnrichLeadRequest } from '../../@types/enrichLeadRequest'
import { isWithinGracePeriod } from '../../utils/lead'
import { fetchValuation } from '../valuation/thunks'
import { EnrichLeadValuationType } from '../../@types/enrichLeadValuationtype'
import { Address } from '../../@types/address'
import { fetchUserInfo } from '../user/thunks'
import { Config } from '../../@types/config'
import {
  LeadResponse,
  ReceiveLeadRequest,
  ReceiveSolarLeadRequest,
  SolarLeadResponse,
} from '../../clients/sendLeadClient'
import { Iv24TrackingData } from '../immoverkauf24/trackingSlice'
import { STEP } from '../../@types/state'
import { updateRoomsNumber } from './leadSlice'
import { AppThunk } from '../reducers'

interface ThunkStateConfig {
  state: {
    lead: Lead
    location: Resource<PropertyLocation>
    user: User
    valuation: Resource<Valuation>
    address: Resource<Address>
    config: Config
    iv24TrackingData: Iv24TrackingData
  }
}

export interface SendLeadThunkRequest {
  successCallback: () => void
  errorCallback: () => void
}

const sendLead = createAsyncThunk<LeadResponse, SendLeadThunkRequest, ThunkStateConfig>(
  'lead/send',
  async (params, thunkAPI) => {
    const {
      lead,
      location,
      user,
      valuation,
      address,
      config: { tealium, coopId, integrationType },
      iv24TrackingData,
    } = thunkAPI.getState()
    const { successCallback, errorCallback } = params
    const leadRequest: ReceiveLeadRequest = {
      lead: { ...lead, duplicateUser: isDuplicateUser() },
      location,
      user,
      address,
      valuation,
      coopId,
      integrationType,
      iv24TrackingData,
    }

    return sendLeadClient
      .submitLead(leadRequest)
      .then((res) => {
        if (!isDuplicateUser()) {
          setRleCookie(res.rawLeadId)
          if (tealium) {
            reportSubmitLeadEvent(res.rawLeadId, 'lead_submit', lead, user, location.data)
          }
        }
        successCallback()
        return res
      })
      .catch((error) => {
        if (tealium) {
          reportEvent(
            {
              pageTitle: 'contactform',
              label: `contact-form_validation-errors`,
              action: 'validation-error',
              category: 'contact-form-errors',
              eventName: 'lead_error',
              inputValue: 'contact-form_validation-errors',
            },
            user,
            location.data?.postcode
          )
        }
        errorCallback()
        throw error
      })
  }
)

export interface EnrichLeadThunkRequest {
  request: EnrichLeadRequest
  pageTitle: string
  enrichValuationType?: EnrichLeadValuationType
  successCallback?: () => void
  errorCallback?: () => void
  valuationForStep?: STEP
}

const enrichLead = createAsyncThunk<void, EnrichLeadThunkRequest, ThunkStateConfig>(
  'lead/enrich',
  async (enrichLeadThunkRequest, thunkAPI) => {
    const { dispatch } = thunkAPI
    const { lead } = thunkAPI.getState()
    const { enrichValuationType, valuationForStep } = enrichLeadThunkRequest

    console.log('enrichLeadThunkRequest', enrichLeadThunkRequest)
    if (isWithinGracePeriod(lead)) {
      thunkAPI.dispatch(
        fetchValuation({
          successCallback: (result) => {
            const newEnrichRequest = { ...enrichLeadThunkRequest }
            newEnrichRequest.request.valuationRange = `${result.meta.range.min}-${result.meta.range.max}`

            if (enrichValuationType === EnrichLeadValuationType.SINGLE_VALUATION) {
              newEnrichRequest.request.valuation = result.value
            }
            dispatch(callEnrichAPI(newEnrichRequest))
          },
          errorCallback: () => {
            dispatch(callEnrichAPI(enrichLeadThunkRequest))
          },
          valuationForStep: valuationForStep,
        })
      )
    }
  }
)

const callEnrichAPI = createAsyncThunk<void, EnrichLeadThunkRequest, ThunkStateConfig>(
  'lead/callEnrichAPI',
  async (enrichLeadThunkRequest, { dispatch, getState }) => {
    const { request, successCallback, errorCallback } = enrichLeadThunkRequest
    const {
      config: { tealium },
      user,
      location,
      address,
    } = getState()

    return sendLeadClient
      .enrichLead(request)
      .then(() => {
        successCallback && successCallback()
        return
      })
      .catch(() => {
        dispatch(fetchUserInfo())
        if (tealium) {
          reportEvent(
            {
              pageTitle: enrichLeadThunkRequest.pageTitle,
              label: 'rich_validation-errors',
              action: 'validation-error',
              category: 'contact-form-errors',
              eventName: 'lead_error',
              inputValue: 'rich_validation-errors',
            },
            user,
            location.data?.postcode,
            address.data?.street
          )
        }
        errorCallback && errorCallback()
      })
  }
)

const sendSolarLead = createAsyncThunk<SolarLeadResponse, SendLeadThunkRequest, ThunkStateConfig>(
  'lead/sendSolarLead',
  async (params, thunkAPI) => {
    const {
      lead,
      location,
      user,
      config: { coopId },
    } = thunkAPI.getState()
    const { successCallback, errorCallback } = params
    const leadRequest: ReceiveSolarLeadRequest = {
      lead,
      location,
      user,
      coopId,
    }

    return sendLeadClient
      .submitSolarLead(leadRequest)
      .then((res) => {
        successCallback()
        return res
      })
      .catch((error) => {
        errorCallback()
        throw error
      })
  }
)

export const setDefaultRoomsBasedOnLivingSpace = (): AppThunk => (dispatch, getState) => {
  const { livingSpace, rooms } = getState().lead
  if (typeof livingSpace !== 'undefined' && typeof rooms === 'undefined') {
    const newRoomsNumber = livingSpace < 100 ? 2 : livingSpace < 150 ? 4 : 5
    dispatch(updateRoomsNumber(newRoomsNumber))
  }
}

export { sendLead, enrichLead, sendSolarLead }
